/**
 * components/Events/EventsPromotions.tsx - Events & Promotions Management
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './EventsPromotions.module.css';
import eventService, {
  Event,
  EventStatus,
  PROMOTION_STATUSES,
  EVENT_STATUSES,
} from '../../services/eventService';
import toastService from '../../services/toastService';
import StatTile from '../Dashboard/StatTile';
import Modal from '../UI/Modal';
import {
  SearchIcon,
  PlusIcon,
  EditIcon,
  DeleteIcon,
  CalendarIcon,
  EventsIcon,
  UsersIcon,
  TrendingUpIcon,
  CheckIcon,
  CloseIcon,
  StadiumIcon,
} from '../UI/Icons';
import { DEFAULT_EVENT_IMAGE } from '../../utils/constants';

type ViewTab = 'promotions' | 'events';

interface CampaignRecord extends Event {
  id?: string;
}

interface EventsPromotionsProps {
  sharedData?: { vendors: number; sections: string[] };
}

interface PromotionFormData {
  title: string;
  description: string;
  promotionType: string;
  status: EventStatus;
  startDate: string;
  endDate: string;
  image: string | null;
  targetAudience: string;
  priority: number;
  actionUrl?: string;
  actionText?: string;
  selectedSections?: string[];
}

interface EventFormData {
  name: string;
  description: string;
  venue: string;
  status: EventStatus;
  startDate: string;
  endDate: string;
  image: string | null;
  teams: string;
  capacity: number;
  ticketsSold: number;
}

interface FormErrors {
  [key: string]: string | undefined;
}

const PROMOTION_TYPES = [
  { value: 'banner', label: 'Banner Ad' },
  { value: 'popup', label: 'Pop-up' },
  { value: 'inline', label: 'Inline' },
  { value: 'sticky', label: 'Sticky Header' },
];

const TARGET_AUDIENCES = [
  { value: 'all', label: 'All Users' },
  { value: 'new', label: 'New Users' },
  { value: 'returning', label: 'Returning Users' },
  { value: 'vip', label: 'VIP Members' },
  { value: 'section-specific', label: 'Section Specific' },
];

const IMAGE_SPECS: Record<string, { width: number; height: number; ratio: string }> = {
  banner: { width: 375, height: 150, ratio: '5:2' },
  popup: { width: 300, height: 400, ratio: '3:4' },
  inline: { width: 345, height: 200, ratio: '16:9' },
  sticky: { width: 375, height: 80, ratio: '15:4' },
};

function getRecordId(record: CampaignRecord): string | undefined {
  return record.id ?? record._id;
}

function normalizeRecord(event: Event): CampaignRecord {
  return { ...event, id: event.id ?? event._id };
}

function formatDate(dateInput: Date | string | undefined): string {
  if (!dateInput) return '—';
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toDatetimeLocal(value: Date | string | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16);
}

function resolvePromotionPayload(data: PromotionFormData): {
  description: string;
  startDate: Date;
  endDate: Date;
  image: string;
} {
  const startDate = new Date(data.startDate);
  const endDate = data.endDate
    ? new Date(data.endDate)
    : new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000);

  return {
    description: data.description.trim(),
    startDate,
    endDate,
    image: data.image?.trim() || DEFAULT_EVENT_IMAGE,
  };
}

function getAverageCTR(promotions: CampaignRecord[]): string {
  const valid = promotions.filter((p) => (p.analytics?.impressions ?? 0) > 0);
  if (valid.length === 0) return '0.0';
  const total = valid.reduce((sum, p) => sum + (p.analytics?.ctr ?? 0), 0);
  return (total / valid.length).toFixed(1);
}

function getStatusLabel(status: EventStatus, tab: ViewTab): string {
  const list = tab === 'promotions' ? PROMOTION_STATUSES : EVENT_STATUSES;
  return list.find((s) => s.value === status)?.label ?? status;
}

function getStatusBadgeClass(status: EventStatus): string {
  switch (status) {
    case 'active':
      return styles.badgeActive;
    case 'scheduled':
      return styles.badgeScheduled;
    case 'paused':
      return styles.badgePaused;
    case 'expired':
      return styles.badgeExpired;
    case 'completed':
      return styles.badgeCompleted;
    case 'cancelled':
      return styles.badgeCancelled;
    default:
      return styles.badgeDraft;
  }
}

function getTypeBadgeClass(type?: string): string {
  switch (type) {
    case 'popup':
      return styles.badgePopup;
    case 'inline':
      return styles.badgeInline;
    case 'sticky':
      return styles.badgeSticky;
    default:
      return styles.badgeBanner;
  }
}

function emitPromotionsUpdated(): void {
  window.dispatchEvent(new CustomEvent('promotionsUpdated'));
}

const EventsPromotions: React.FC<EventsPromotionsProps> = ({ sharedData }) => {
  const [records, setRecords] = useState<CampaignRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSecondary, setFilterSecondary] = useState('all');
  const [viewTab, setViewTab] = useState<ViewTab>('promotions');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<CampaignRecord | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CampaignRecord | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const data = await eventService.getAll();
      setRecords(data.map(normalizeRecord));
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load events & promotions:', err);
      setError('Failed to load data. Please try again.');
      toastService.error('Failed to load events & promotions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const promotions = useMemo(
    () => records.filter((r) => r.type === 'promotion'),
    [records],
  );

  const gameEvents = useMemo(
    () => records.filter((r) => r.type === 'event'),
    [records],
  );

  const tabRecords = viewTab === 'promotions' ? promotions : gameEvents;

  const venueOptions = useMemo(() => {
    const venues = new Set<string>();
    gameEvents.forEach((e) => {
      if (e.eventDetails?.venue) venues.add(e.eventDetails.venue);
    });
    return Array.from(venues).sort();
  }, [gameEvents]);

  const hasActiveFilters =
    searchTerm.trim() !== '' ||
    filterStatus !== 'all' ||
    filterSecondary !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterSecondary('all');
  };

  const filteredRecords = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return tabRecords
      .filter((record) => {
        const title = (record.title ?? record.name ?? '').toLowerCase();
        const desc = (record.description ?? '').toLowerCase();
        const matchesSearch = !term || title.includes(term) || desc.includes(term);
        const matchesStatus = filterStatus === 'all' || record.status === filterStatus;

        let matchesSecondary = true;
        if (viewTab === 'promotions' && filterSecondary !== 'all') {
          matchesSecondary = record.promotionType === filterSecondary;
        }
        if (viewTab === 'events' && filterSecondary !== 'all') {
          matchesSecondary = record.eventDetails?.venue === filterSecondary;
        }

        return matchesSearch && matchesStatus && matchesSecondary;
      })
      .sort((a, b) => {
        let aVal: string | number | Date = '';
        let bVal: string | number | Date = '';

        if (sortBy === 'startDate' || sortBy === 'endDate' || sortBy === 'createdAt') {
          aVal = new Date((a[sortBy as keyof CampaignRecord] as string) ?? 0);
          bVal = new Date((b[sortBy as keyof CampaignRecord] as string) ?? 0);
        } else if (sortBy === 'analytics.clicks') {
          aVal = a.analytics?.clicks ?? 0;
          bVal = b.analytics?.clicks ?? 0;
        } else if (sortBy === 'analytics.ctr') {
          aVal = a.analytics?.ctr ?? 0;
          bVal = b.analytics?.ctr ?? 0;
        } else if (sortBy === 'title') {
          aVal = (a.title ?? a.name ?? '').toLowerCase();
          bVal = (b.title ?? b.name ?? '').toLowerCase();
        } else if (sortBy === 'capacity') {
          aVal = a.eventDetails?.capacity ?? 0;
          bVal = b.eventDetails?.capacity ?? 0;
        } else {
          aVal = String(a[sortBy as keyof CampaignRecord] ?? '');
          bVal = String(b[sortBy as keyof CampaignRecord] ?? '');
        }

        if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
      });
  }, [tabRecords, searchTerm, filterStatus, filterSecondary, sortBy, sortOrder, viewTab]);

  const promotionStats = useMemo(
    () => ({
      total: promotions.length,
      active: promotions.filter((p) => p.status === 'active').length,
      clicks: promotions.reduce((sum, p) => sum + (p.analytics?.clicks ?? 0), 0),
      avgCtr: getAverageCTR(promotions),
    }),
    [promotions],
  );

  const eventStats = useMemo(() => {
    const totalCapacity = gameEvents.reduce(
      (sum, e) => sum + (e.eventDetails?.capacity ?? 0),
      0,
    );
    const ticketsSold = gameEvents.reduce(
      (sum, e) => sum + (e.eventDetails?.ticketsSold ?? 0),
      0,
    );
    const sellThrough =
      totalCapacity > 0 ? `${((ticketsSold / totalCapacity) * 100).toFixed(0)}% sold` : undefined;

    return {
      upcoming: gameEvents.filter((e) => e.status === 'scheduled').length,
      live: gameEvents.filter((e) => e.status === 'active').length,
      totalCapacity,
      ticketsSold,
      sellThrough,
    };
  }, [gameEvents]);

  const updateRecordInState = (updated: Event) => {
    const normalized = normalizeRecord(updated);
    setRecords((prev) =>
      prev.map((r) => (getRecordId(r) === getRecordId(normalized) ? normalized : r)),
    );
  };

  const patchStatus = async (record: CampaignRecord, status: EventStatus) => {
    const id = getRecordId(record);
    if (!id) return;

    try {
      const updated = await eventService.update(id, { status });
      updateRecordInState(updated);
      emitPromotionsUpdated();
      toastService.success(`Status updated to ${getStatusLabel(status, record.type === 'event' ? 'events' : 'promotions')}`);
    } catch (err) {
      console.error('Failed to update status:', err);
      toastService.error('Failed to update status');
    }
  };

  const handleActivate = (record: CampaignRecord) => patchStatus(record, 'active');
  const handlePause = (record: CampaignRecord) => patchStatus(record, 'paused');
  const handleMarkCompleted = (record: CampaignRecord) => patchStatus(record, 'completed');
  const handleCancel = (record: CampaignRecord) => patchStatus(record, 'cancelled');

  const handleSavePromotion = async (data: PromotionFormData) => {
    setSaving(true);
    try {
      const resolved = resolvePromotionPayload(data);
      const payload: Partial<Event> = {
        name: data.title,
        title: data.title,
        type: 'promotion',
        description: resolved.description,
        promotionType: data.promotionType as Event['promotionType'],
        status: data.status,
        startDate: resolved.startDate,
        endDate: resolved.endDate,
        image: resolved.image,
        targetAudience: data.targetAudience as Event['targetAudience'],
        actionUrl: data.actionUrl,
        actionText: data.actionText,
        priority: data.priority,
      };

      if (editingRecord) {
        const id = getRecordId(editingRecord);
        if (!id) throw new Error('Missing record id');
        const updated = await eventService.update(id, payload);
        updateRecordInState(updated);
        toastService.success('Promotion updated');
      } else {
        const created = await eventService.create({
          ...payload,
          name: data.title,
          type: 'promotion',
          startDate: resolved.startDate,
          endDate: resolved.endDate,
          status: data.status,
          analytics: { impressions: 0, clicks: 0, ctr: 0, conversions: 0 },
        } as Omit<Event, '_id'>);
        setRecords((prev) => [...prev, normalizeRecord(created)]);
        toastService.success('Promotion created');
      }

      emitPromotionsUpdated();
      setShowCreateModal(false);
      setEditingRecord(null);
    } catch (err) {
      console.error('Failed to save promotion:', err);
      toastService.error('Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEvent = async (data: EventFormData) => {
    setSaving(true);
    try {
      const teams = data.teams
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const payload: Partial<Event> = {
        name: data.name,
        type: 'event',
        description: data.description,
        status: data.status,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        image: data.image ?? undefined,
        eventDetails: {
          teams,
          venue: data.venue,
          capacity: data.capacity,
          ticketsSold: data.ticketsSold,
        },
      };

      if (editingRecord) {
        const id = getRecordId(editingRecord);
        if (!id) throw new Error('Missing record id');
        const updated = await eventService.update(id, payload);
        updateRecordInState(updated);
        toastService.success('Event updated');
      } else {
        const created = await eventService.create({
          ...payload,
          name: data.name,
          type: 'event',
          startDate: new Date(data.startDate),
          endDate: new Date(data.endDate),
          status: data.status,
        } as Omit<Event, '_id'>);
        setRecords((prev) => [...prev, normalizeRecord(created)]);
        toastService.success('Event created');
      }

      emitPromotionsUpdated();
      setShowCreateModal(false);
      setEditingRecord(null);
    } catch (err) {
      console.error('Failed to save event:', err);
      toastService.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = getRecordId(pendingDelete);
    if (!id) return;

    setConfirmLoading(true);
    try {
      await eventService.delete(id);
      setRecords((prev) => prev.filter((r) => getRecordId(r) !== id));
      emitPromotionsUpdated();
      toastService.success(
        pendingDelete.type === 'event' ? 'Event deleted' : 'Promotion deleted',
      );
      setPendingDelete(null);
    } catch (err) {
      console.error('Failed to delete:', err);
      toastService.error('Failed to delete');
    } finally {
      setConfirmLoading(false);
    }
  };

  const trackPromotionClick = async (promotionId: string) => {
    try {
      const result = await eventService.trackClick(promotionId);
      if (result.analytics) {
        setRecords((prev) =>
          prev.map((r) =>
            getRecordId(r) === promotionId
              ? { ...r, analytics: result.analytics }
              : r,
          ),
        );
      } else {
        const refreshed = await eventService.getById(promotionId);
        updateRecordInState(refreshed);
      }
      toastService.success('Click tracked');
    } catch (err) {
      console.error('Failed to track click:', err);
      toastService.error('Failed to track click');
    }
  };

  const openCreate = () => {
    setEditingRecord(null);
    setShowCreateModal(true);
  };

  const openEdit = (record: CampaignRecord) => {
    setEditingRecord(record);
    setShowCreateModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setShowCreateModal(false);
    setEditingRecord(null);
  };

  const secondsAgo =
    lastUpdated != null
      ? Math.max(0, Math.floor((Date.now() - lastUpdated.getTime()) / 1000))
      : null;

  const isTrulyEmpty = tabRecords.length === 0;
  const isFilteredEmpty = !isTrulyEmpty && filteredRecords.length === 0;
  const showSkeleton = loading && records.length === 0;

  const formModalOpen = showCreateModal || !!editingRecord;
  const editingTab: ViewTab =
    editingRecord?.type === 'event' ? 'events' : 'promotions';
  const activeFormTab = editingRecord ? editingTab : viewTab;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Events & Promotions</h1>
          <p className={styles.subtitle}>
            {viewTab === 'promotions'
              ? 'Manage promotional campaigns and track mobile engagement'
              : 'Schedule game events and monitor ticket sell-through'}
            {sharedData && (
              <span className={styles.integrationInfo}>
                {' '}
                · {sharedData.vendors} vendors · {sharedData.sections?.length ?? 0} sections
              </span>
            )}
            {secondsAgo != null && (
              <span className={styles.updatedAt}>
                {' '}
                · Updated {secondsAgo}s ago
                {refreshing ? ' (refreshing…)' : ''}
              </span>
            )}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            onClick={() => loadAll(true)}
            className={styles.refreshButton}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" onClick={openCreate} className={styles.createButton}>
            <PlusIcon size={16} />
            {viewTab === 'promotions' ? 'New Promotion' : 'New Event'}
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorAlert}>
          <p>{error}</p>
          <button type="button" onClick={() => setError(null)} className={styles.closeButton} aria-label="Dismiss">
            <CloseIcon size={18} />
          </button>
        </div>
      )}

      <div className={styles.statTileRow}>
        {viewTab === 'promotions' ? (
          <>
            <StatTile
              icon={<EventsIcon size={16} />}
              label="Total Promotions"
              value={promotionStats.total}
              loading={showSkeleton}
            />
            <StatTile
              icon={<CheckIcon size={16} />}
              label="Active Campaigns"
              value={promotionStats.active}
              trendTone="success"
              loading={showSkeleton}
            />
            <StatTile
              icon={<TrendingUpIcon size={16} />}
              label="Total Clicks"
              value={promotionStats.clicks.toLocaleString()}
              loading={showSkeleton}
            />
            <StatTile
              icon={<TrendingUpIcon size={16} />}
              label="Avg CTR"
              value={`${promotionStats.avgCtr}%`}
              loading={showSkeleton}
            />
          </>
        ) : (
          <>
            <StatTile
              icon={<CalendarIcon size={16} />}
              label="Upcoming Events"
              value={eventStats.upcoming}
              loading={showSkeleton}
            />
            <StatTile
              icon={<EventsIcon size={16} />}
              label="Live Now"
              value={eventStats.live}
              trendTone="success"
              loading={showSkeleton}
            />
            <StatTile
              icon={<StadiumIcon size={16} />}
              label="Total Capacity"
              value={eventStats.totalCapacity.toLocaleString()}
              loading={showSkeleton}
            />
            <StatTile
              icon={<UsersIcon size={16} />}
              label="Tickets Sold"
              value={eventStats.ticketsSold.toLocaleString()}
              subtitle={eventStats.sellThrough}
              loading={showSkeleton}
            />
          </>
        )}
      </div>

      <div className={styles.tabStrip} role="tablist" aria-label="Content type">
        <button
          type="button"
          role="tab"
          aria-selected={viewTab === 'promotions'}
          className={`${styles.tab} ${viewTab === 'promotions' ? styles.tabActive : ''}`}
          onClick={() => {
            setViewTab('promotions');
            setFilterStatus('all');
            setFilterSecondary('all');
          }}
        >
          Promotions
          <span className={styles.tabCount}>{promotions.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={viewTab === 'events'}
          className={`${styles.tab} ${viewTab === 'events' ? styles.tabActive : ''}`}
          onClick={() => {
            setViewTab('events');
            setFilterStatus('all');
            setFilterSecondary('all');
          }}
        >
          Game Events
          <span className={styles.tabCount}>{gameEvents.length}</span>
        </button>
      </div>

      <div className={styles.filterRow}>
        <div className={styles.searchWrap}>
          <SearchIcon size={18} className={styles.searchIcon} />
          <input
            type="search"
            className={styles.searchInput}
            placeholder={viewTab === 'promotions' ? 'Search promotions…' : 'Search events…'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className={styles.filterSelect}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="all">All status</option>
          {(viewTab === 'promotions' ? PROMOTION_STATUSES : EVENT_STATUSES).map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          className={styles.filterSelect}
          value={filterSecondary}
          onChange={(e) => setFilterSecondary(e.target.value)}
          aria-label={viewTab === 'promotions' ? 'Filter by type' : 'Filter by venue'}
        >
          {viewTab === 'promotions' ? (
            <>
              <option value="all">All types</option>
              {PROMOTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </>
          ) : (
            <>
              <option value="all">All venues</option>
              {venueOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </>
          )}
        </select>
        <select
          className={styles.filterSelect}
          value={`${sortBy}-${sortOrder}`}
          onChange={(e) => {
            const [field, order] = e.target.value.split('-');
            setSortBy(field);
            setSortOrder(order as 'asc' | 'desc');
          }}
          aria-label="Sort"
        >
          {viewTab === 'promotions' ? (
            <>
              <option value="createdAt-desc">Newest first</option>
              <option value="title-asc">Title A–Z</option>
              <option value="analytics.clicks-desc">Most clicked</option>
              <option value="analytics.ctr-desc">Highest CTR</option>
              <option value="startDate-desc">Recent start</option>
            </>
          ) : (
            <>
              <option value="startDate-desc">Upcoming first</option>
              <option value="title-asc">Name A–Z</option>
              <option value="capacity-desc">Largest capacity</option>
              <option value="createdAt-desc">Newest first</option>
            </>
          )}
        </select>
      </div>

      {showSkeleton ? (
        <SkeletonGrid />
      ) : isTrulyEmpty ? (
        <EmptyState tab={viewTab} filtered={false} onCreate={openCreate} />
      ) : isFilteredEmpty ? (
        <EmptyState tab={viewTab} filtered onCreate={openCreate} onClearFilters={clearFilters} />
      ) : (
        <div className={styles.cardGrid}>
          {filteredRecords.map((record) => {
            const id = getRecordId(record);
            if (!id) return null;
            return record.type === 'promotion' ? (
              <PromotionCard
                key={id}
                promotion={record}
                onEdit={() => openEdit(record)}
                onDelete={() => setPendingDelete(record)}
                onActivate={() => handleActivate(record)}
                onPause={() => handlePause(record)}
                onTestClick={() => trackPromotionClick(id)}
              />
            ) : (
              <EventCard
                key={id}
                event={record}
                onEdit={() => openEdit(record)}
                onDelete={() => setPendingDelete(record)}
                onActivate={() => handleActivate(record)}
                onComplete={() => handleMarkCompleted(record)}
                onCancel={() => handleCancel(record)}
              />
            );
          })}
        </div>
      )}

      <Modal
        isOpen={formModalOpen}
        onClose={closeFormModal}
        title={
          editingRecord
            ? activeFormTab === 'events'
              ? 'Edit Event'
              : 'Edit Promotion'
            : activeFormTab === 'events'
              ? 'Create Event'
              : 'Create Promotion'
        }
        size="xl"
        footer={null}
      >
        {activeFormTab === 'promotions' ? (
          <PromotionForm
            promotion={editingRecord?.type === 'promotion' ? editingRecord : null}
            sections={sharedData?.sections ?? []}
            saving={saving}
            onSave={handleSavePromotion}
            onCancel={closeFormModal}
          />
        ) : (
          <EventForm
            event={editingRecord?.type === 'event' ? editingRecord : null}
            saving={saving}
            onSave={handleSaveEvent}
            onCancel={closeFormModal}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!pendingDelete}
        onClose={() => !confirmLoading && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.type === 'event' ? 'event' : 'promotion'}?`}
        size="sm"
        footer={
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setPendingDelete(null)}
              disabled={confirmLoading}
            >
              Keep
            </button>
            <button
              type="button"
              className={styles.dangerButton}
              onClick={confirmDelete}
              disabled={confirmLoading}
            >
              {confirmLoading ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        }
      >
        <p className={styles.confirmMessage}>
          Are you sure you want to delete{' '}
          <strong>{pendingDelete?.title ?? pendingDelete?.name}</strong>?
          {pendingDelete?.type === 'promotion' && ' All analytics data will be lost.'}
        </p>
      </Modal>
    </div>
  );
};

/* ── Sub-components ── */

function SkeletonGrid() {
  return (
    <div className={styles.cardGrid}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={styles.skeletonCard} aria-hidden="true">
          <div className={`${styles.skeletonBlock} ${styles.skeletonImage}`} />
          <div className={styles.skeletonBody}>
            <div className={`${styles.skeletonBlock} ${styles.skeletonLine}`} />
            <div className={`${styles.skeletonBlock} ${styles.skeletonLineShort}`} />
            <div className={`${styles.skeletonBlock} ${styles.skeletonLine}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  tab,
  filtered,
  onCreate,
  onClearFilters,
}: {
  tab: ViewTab;
  filtered: boolean;
  onCreate: () => void;
  onClearFilters?: () => void;
}) {
  const isPromo = tab === 'promotions';
  return (
    <div className={styles.emptyState}>
      <EventsIcon size={48} className={styles.emptyIcon} />
      <h3 className={styles.emptyStateTitle}>
        {filtered
          ? `No matching ${isPromo ? 'promotions' : 'events'}`
          : `No ${isPromo ? 'promotions' : 'events'} yet`}
      </h3>
      <p className={styles.emptyStateDescription}>
        {filtered
          ? 'Try adjusting your search or filters.'
          : isPromo
            ? 'Create your first promotional campaign to engage mobile users.'
            : 'Schedule your first game event to start tracking capacity and tickets.'}
      </p>
      <div className={styles.emptyActions}>
        {!filtered && (
          <button type="button" onClick={onCreate} className={styles.primaryButton}>
            <PlusIcon size={16} />
            {isPromo ? 'Create Promotion' : 'Schedule Event'}
          </button>
        )}
        {filtered && onClearFilters && (
          <button type="button" onClick={onClearFilters} className={styles.secondaryButton}>
            Clear Filters
          </button>
        )}
      </div>
    </div>
  );
}

interface PromotionCardProps {
  promotion: CampaignRecord;
  onEdit: () => void;
  onDelete: () => void;
  onActivate: () => void;
  onPause: () => void;
  onTestClick: () => void;
}

function PromotionCard({
  promotion,
  onEdit,
  onDelete,
  onActivate,
  onPause,
  onTestClick,
}: PromotionCardProps) {
  const promoType = promotion.promotionType ?? 'banner';
  return (
    <article className={styles.card}>
      <div className={styles.cardImageWrap}>
        <img
          src={
            promotion.image && !promotion.image.startsWith('blob:')
              ? promotion.image
              : DEFAULT_EVENT_IMAGE
          }
          alt={promotion.title ?? promotion.name}
          className={styles.cardImage}
          onError={(e) => {
            (e.target as HTMLImageElement).src = DEFAULT_EVENT_IMAGE;
          }}
        />
        <div className={styles.imageBadges}>
          <span className={`${styles.badge} ${getTypeBadgeClass(promoType)}`}>
            {PROMOTION_TYPES.find((t) => t.value === promoType)?.label ?? promoType}
          </span>
        </div>
      </div>
      <div className={styles.cardBody}>
        <h3 className={styles.cardTitle}>{promotion.title ?? promotion.name}</h3>
        {promotion.description && (
          <p className={styles.cardDescription}>{promotion.description}</p>
        )}
        <div className={styles.cardMeta}>
          <span className={`${styles.badge} ${getStatusBadgeClass(promotion.status)}`}>
            {getStatusLabel(promotion.status, 'promotions')}
          </span>
          <span className={styles.dateBadge}>
            {formatDate(promotion.startDate)} – {formatDate(promotion.endDate)}
          </span>
        </div>
        <div className={styles.analyticsGrid}>
          <div className={styles.analyticsItem}>
            <span className={styles.analyticsValue}>
              {(promotion.analytics?.impressions ?? 0).toLocaleString()}
            </span>
            <span className={styles.analyticsLabel}>Impressions</span>
          </div>
          <div className={styles.analyticsItem}>
            <span className={styles.analyticsValue}>
              {(promotion.analytics?.clicks ?? 0).toLocaleString()}
            </span>
            <span className={styles.analyticsLabel}>Clicks</span>
          </div>
          <div className={styles.analyticsItem}>
            <span className={styles.analyticsValue}>
              {(promotion.analytics?.ctr ?? 0).toFixed(2)}%
            </span>
            <span className={styles.analyticsLabel}>CTR</span>
          </div>
          <div className={styles.analyticsItem}>
            <span className={styles.analyticsValue}>
              {(promotion.analytics?.conversions ?? 0).toLocaleString()}
            </span>
            <span className={styles.analyticsLabel}>Conversions</span>
          </div>
        </div>
        <div className={styles.cardActions}>
          {promotion.status === 'active' ? (
            <button type="button" onClick={onPause} className={styles.lifecycleButton}>
              Pause
            </button>
          ) : promotion.status !== 'expired' ? (
            <button type="button" onClick={onActivate} className={styles.lifecycleButtonPrimary}>
              Activate
            </button>
          ) : null}
          <button type="button" onClick={onTestClick} className={styles.devLink} title="Test click tracking">
            [dev] Test click
          </button>
          <div className={styles.iconActions}>
            <button type="button" onClick={onEdit} className={styles.actionButton} aria-label="Edit">
              <EditIcon size={16} />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className={`${styles.actionButton} ${styles.deleteButton}`}
              aria-label="Delete"
            >
              <DeleteIcon size={16} />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

interface EventCardProps {
  event: CampaignRecord;
  onEdit: () => void;
  onDelete: () => void;
  onActivate: () => void;
  onComplete: () => void;
  onCancel: () => void;
}

function EventCard({
  event,
  onEdit,
  onDelete,
  onActivate,
  onComplete,
  onCancel,
}: EventCardProps) {
  const capacity = event.eventDetails?.capacity ?? 0;
  const sold = event.eventDetails?.ticketsSold ?? 0;
  const pct = capacity > 0 ? Math.min(100, (sold / capacity) * 100) : 0;
  const teams = event.eventDetails?.teams ?? [];

  return (
    <article className={styles.card}>
      <div className={styles.cardImageWrap}>
        <img
          src={
            event.image && !event.image.startsWith('blob:')
              ? event.image
              : DEFAULT_EVENT_IMAGE
          }
          alt={event.name}
          className={styles.cardImage}
          onError={(e) => {
            (e.target as HTMLImageElement).src = DEFAULT_EVENT_IMAGE;
          }}
        />
      </div>
      <div className={styles.cardBody}>
        <h3 className={styles.cardTitle}>{event.name}</h3>
        {event.eventDetails?.venue && (
          <p className={styles.venueLine}>
            <StadiumIcon size={14} />
            {event.eventDetails.venue}
          </p>
        )}
        {event.description && <p className={styles.cardDescription}>{event.description}</p>}
        {teams.length > 0 && (
          <div className={styles.teamChips}>
            {teams.map((team) => (
              <span key={team} className={styles.teamChip}>
                {team}
              </span>
            ))}
          </div>
        )}
        <div className={styles.cardMeta}>
          <span className={`${styles.badge} ${getStatusBadgeClass(event.status)}`}>
            {getStatusLabel(event.status, 'events')}
          </span>
          <span className={styles.dateBadge}>
            {formatDate(event.startDate)} – {formatDate(event.endDate)}
          </span>
        </div>
        {capacity > 0 && (
          <div className={styles.capacitySection}>
            <div className={styles.capacityHeader}>
              <span className={styles.capacityLabel}>Tickets</span>
              <span className={styles.capacityNumbers}>
                {sold.toLocaleString()} / {capacity.toLocaleString()}
              </span>
            </div>
            <div className={styles.capacityBar} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className={styles.capacityFill} style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        <div className={styles.cardActions}>
          {event.status === 'active' && (
            <button type="button" onClick={onComplete} className={styles.lifecycleButtonPrimary}>
              Mark Completed
            </button>
          )}
          {event.status === 'scheduled' && (
            <button type="button" onClick={onActivate} className={styles.lifecycleButtonPrimary}>
              Go Live
            </button>
          )}
          {event.status !== 'cancelled' && event.status !== 'completed' && (
            <button type="button" onClick={onCancel} className={styles.lifecycleButton}>
              Cancel
            </button>
          )}
          <div className={styles.iconActions}>
            <button type="button" onClick={onEdit} className={styles.actionButton} aria-label="Edit">
              <EditIcon size={16} />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className={`${styles.actionButton} ${styles.deleteButton}`}
              aria-label="Delete"
            >
              <DeleteIcon size={16} />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

interface PromotionFormProps {
  promotion: CampaignRecord | null;
  sections: string[];
  saving: boolean;
  onSave: (data: PromotionFormData) => void;
  onCancel: () => void;
}

function PromotionForm({ promotion, sections, saving, onSave, onCancel }: PromotionFormProps) {
  const [formData, setFormData] = useState<PromotionFormData>({
    title: promotion?.title ?? promotion?.name ?? '',
    description: promotion?.description ?? '',
    promotionType: promotion?.promotionType ?? 'banner',
    status: promotion?.status ?? 'draft',
    image: promotion?.image ?? '',
    startDate: toDatetimeLocal(promotion?.startDate),
    endDate: toDatetimeLocal(promotion?.endDate),
    targetAudience: promotion?.targetAudience ?? 'all',
    actionUrl: promotion?.actionUrl ?? '',
    actionText: promotion?.actionText ?? 'Learn More',
    priority: promotion?.priority ?? 1,
    selectedSections: [],
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const validate = (): boolean => {
    const errors: FormErrors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.startDate) errors.startDate = 'Start date is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave(formData);
  };

  const specs = IMAGE_SPECS[formData.promotionType] ?? IMAGE_SPECS.banner;

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formGrid}>
        <div className={styles.formColumn}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className={`${styles.formInput} ${formErrors.title ? styles.formInputError : ''}`}
            />
            {formErrors.title && <span className={styles.errorText}>{formErrors.title}</span>}
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={styles.formTextarea}
              rows={3}
              placeholder="Optional — shown on the promotion card"
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Type</label>
              <select
                value={formData.promotionType}
                onChange={(e) => setFormData({ ...formData, promotionType: e.target.value })}
                className={styles.formSelect}
              >
                {PROMOTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Status</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value as EventStatus })
                }
                className={styles.formSelect}
              >
                {PROMOTION_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Start *</label>
              <input
                type="datetime-local"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className={`${styles.formInput} ${formErrors.startDate ? styles.formInputError : ''}`}
              />
              {formErrors.startDate && (
                <span className={styles.errorText}>{formErrors.startDate}</span>
              )}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>End</label>
              <input
                type="datetime-local"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className={styles.formInput}
              />
              <p className={styles.fieldHint}>Optional — defaults to 30 days after start</p>
            </div>
          </div>
        </div>
        <div className={styles.formColumn}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Image URL{' '}
              <span className={styles.imageSpecs}>
                ({specs.width}×{specs.height}px, {specs.ratio})
              </span>
            </label>
            <div className={styles.imageRow}>
              <img
                src={
                  formData.image && !formData.image.startsWith('blob:')
                    ? formData.image
                    : DEFAULT_EVENT_IMAGE
                }
                alt="Preview"
                className={styles.imageThumb}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = DEFAULT_EVENT_IMAGE;
                }}
              />
              <div className={styles.imageUrlField}>
                <input
                  type="url"
                  value={formData.image ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, image: e.target.value.trim() || null })
                  }
                  placeholder="https://example.com/image.jpg"
                  className={styles.formInput}
                />
                {formData.image && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, image: null })}
                    className={styles.clearUrlBtn}
                  >
                    <CloseIcon size={14} /> Clear
                  </button>
                )}
                <p className={styles.fieldHint}>Optional — uses placeholder image if blank</p>
              </div>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Target Audience</label>
            <select
              value={formData.targetAudience}
              onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
              className={styles.formSelect}
            >
              {TARGET_AUDIENCES.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
          {formData.targetAudience === 'section-specific' && sections.length > 0 && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Sections</label>
              <select
                multiple
                value={formData.selectedSections}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                  setFormData({ ...formData, selectedSections: selected });
                }}
                className={styles.formSelect}
              >
                {sections.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Action URL</label>
              <input
                type="text"
                value={formData.actionUrl}
                onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
                className={styles.formInput}
                placeholder="/menu or https://…"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Action Text</label>
              <input
                type="text"
                value={formData.actionText}
                onChange={(e) => setFormData({ ...formData, actionText: e.target.value })}
                className={styles.formInput}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Priority (1–10)</label>
            <input
              type="number"
              min={1}
              max={10}
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 1 })
              }
              className={styles.formInput}
            />
          </div>
        </div>
      </div>
      <div className={styles.modalActions}>
        <button type="button" onClick={onCancel} className={styles.secondaryButton} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className={styles.primaryButton} disabled={saving}>
          {saving ? 'Saving…' : promotion ? 'Update Promotion' : 'Create Promotion'}
        </button>
      </div>
    </form>
  );
}

interface EventFormProps {
  event: CampaignRecord | null;
  saving: boolean;
  onSave: (data: EventFormData) => void;
  onCancel: () => void;
}

function EventForm({ event, saving, onSave, onCancel }: EventFormProps) {
  const [formData, setFormData] = useState<EventFormData>({
    name: event?.name ?? '',
    description: event?.description ?? '',
    venue: event?.eventDetails?.venue ?? '',
    status: event?.status ?? 'draft',
    startDate: toDatetimeLocal(event?.startDate),
    endDate: toDatetimeLocal(event?.endDate),
    image: event?.image ?? '',
    teams: (event?.eventDetails?.teams ?? []).join(', '),
    capacity: event?.eventDetails?.capacity ?? 0,
    ticketsSold: event?.eventDetails?.ticketsSold ?? 0,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const validate = (): boolean => {
    const errors: FormErrors = {};
    if (!formData.name.trim()) errors.name = 'Event name is required';
    if (!formData.venue.trim()) errors.venue = 'Venue is required';
    if (!formData.startDate) errors.startDate = 'Start date is required';
    if (!formData.endDate) errors.endDate = 'End date is required';
    if (formData.capacity < 0) errors.capacity = 'Capacity must be zero or greater';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formGrid}>
        <div className={styles.formColumn}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Event Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`${styles.formInput} ${formErrors.name ? styles.formInputError : ''}`}
              placeholder="e.g. Home vs Away"
            />
            {formErrors.name && <span className={styles.errorText}>{formErrors.name}</span>}
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={styles.formTextarea}
              rows={3}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Venue *</label>
            <input
              type="text"
              value={formData.venue}
              onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
              className={`${styles.formInput} ${formErrors.venue ? styles.formInputError : ''}`}
              placeholder="e.g. Main Stadium"
            />
            {formErrors.venue && <span className={styles.errorText}>{formErrors.venue}</span>}
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Teams</label>
            <input
              type="text"
              value={formData.teams}
              onChange={(e) => setFormData({ ...formData, teams: e.target.value })}
              className={styles.formInput}
              placeholder="Home Team, Away Team"
            />
            <p className={styles.fieldHint}>Comma-separated team names</p>
          </div>
        </div>
        <div className={styles.formColumn}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Status</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value as EventStatus })
                }
                className={styles.formSelect}
              >
                {EVENT_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Capacity</label>
              <input
                type="number"
                min={0}
                value={formData.capacity}
                onChange={(e) =>
                  setFormData({ ...formData, capacity: parseInt(e.target.value, 10) || 0 })
                }
                className={styles.formInput}
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Start *</label>
              <input
                type="datetime-local"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className={`${styles.formInput} ${formErrors.startDate ? styles.formInputError : ''}`}
              />
              {formErrors.startDate && (
                <span className={styles.errorText}>{formErrors.startDate}</span>
              )}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>End *</label>
              <input
                type="datetime-local"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className={`${styles.formInput} ${formErrors.endDate ? styles.formInputError : ''}`}
              />
              {formErrors.endDate && (
                <span className={styles.errorText}>{formErrors.endDate}</span>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Tickets Sold</label>
            <input
              type="number"
              min={0}
              value={formData.ticketsSold}
              onChange={(e) =>
                setFormData({ ...formData, ticketsSold: parseInt(e.target.value, 10) || 0 })
              }
              className={styles.formInput}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Image URL</label>
            <div className={styles.imageRow}>
              <img
                src={
                  formData.image && !formData.image.startsWith('blob:')
                    ? formData.image
                    : DEFAULT_EVENT_IMAGE
                }
                alt="Preview"
                className={styles.imageThumb}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = DEFAULT_EVENT_IMAGE;
                }}
              />
              <input
                type="url"
                value={formData.image ?? ''}
                onChange={(e) =>
                  setFormData({ ...formData, image: e.target.value.trim() || null })
                }
                placeholder="https://example.com/image.jpg"
                className={styles.formInput}
              />
            </div>
            <p className={styles.fieldHint}>Optional — defaults to placeholder if blank</p>
          </div>
        </div>
      </div>
      <div className={styles.modalActions}>
        <button type="button" onClick={onCancel} className={styles.secondaryButton} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className={styles.primaryButton} disabled={saving}>
          {saving ? 'Saving…' : event ? 'Update Event' : 'Create Event'}
        </button>
      </div>
    </form>
  );
}

export default EventsPromotions;
